"use client";

import { useLayoutEffect, useRef } from "react";

/**
 * Walks backward through the rendered text (skipping `skip` — the closing
 * icon element itself) and returns the bounding rect of the last character
 * that actually rendered with visible size. Trailing collapsed whitespace or
 * zero-width characters can be the literal last character of the last text
 * node; measuring that instead of the last real glyph would pin the icon to
 * whatever stray position that invisible character rendered at.
 */
function findLastVisibleCharRect(root: Node, skip: Node): DOMRect | null {
    const range = document.createRange();

    function walk(node: Node): DOMRect | null {
        for (let i = node.childNodes.length - 1; i >= 0; i--) {
            const child = node.childNodes[i];
            if (child === skip) continue;
            if (child.nodeType === Node.TEXT_NODE) {
                const text = child.textContent ?? "";
                for (let charIndex = text.length - 1; charIndex >= 0; charIndex--) {
                    range.setStart(child, charIndex);
                    range.setEnd(child, charIndex + 1);
                    const rect = range.getBoundingClientRect();
                    // height is basically always nonzero here (it's the line's
                    // height, not the glyph's), so only width tells us whether
                    // this position actually has visible ink.
                    if (rect.width > 0) return rect;
                }
                continue;
            }
            if (child.nodeType === Node.ELEMENT_NODE) {
                const found = walk(child);
                if (found) return found;
            }
        }
        return null;
    }

    return walk(root);
}

export default function BlockQuote({ children, wide }: any) {
    const containerRef = useRef<HTMLSpanElement>(null);
    const iconRef = useRef<HTMLSpanElement>(null);

    useLayoutEffect(() => {
        const container = containerRef.current;
        const icon = iconRef.current;
        if (!container || !icon) return;

        const reposition = () => {
            // Reset to normal flow before measuring, so a re-run (resize) never
            // compounds an offset computed on top of a previous correction.
            icon.style.position = "";
            icon.style.top = "";
            icon.style.left = "";
            icon.style.marginLeft = "";
            icon.style.transform = "";

            const lastCharRect = findLastVisibleCharRect(container, icon);
            if (!lastCharRect) return;

            const iconRect = icon.getBoundingClientRect();

            // A same-line icon and character still differ by several px in
            // `top` — the icon is a vertical-align: middle inline-block box,
            // the character rect is the glyph's own (baseline-ish) box, and
            // those two boxes are anchored differently within the line. Only
            // treat it as wrapped once the gap is close to a full line, or a
            // same-line quote (like a short one-line testimonial) gets
            // needlessly kicked into position: absolute, which then reflows
            // the text — since removing the icon from flow frees up the
            // width it used to occupy — into a genuinely different last-line
            // break than what was just measured.
            const lineHeight = parseFloat(getComputedStyle(container).lineHeight) || 32;
            const wrapped = Math.abs(iconRect.top - lastCharRect.top) > lineHeight * 0.6;

            // If the icon didn't land on the same line as the last real
            // character, it wrapped alone — pin it to where the text actually
            // ends instead of leaving it floating on its own line.
            if (wrapped) {
                // Position (and clamp) relative to the <blockquote> itself, not
                // the inline text span — the span shrink-wraps tightly around
                // the rendered text with zero buffer, so clamping against it
                // left no room for the gap at all on a nearly full-width last
                // line. The blockquote is the actual block-level box with real
                // width to work with.
                const positionedAncestor = icon.closest("blockquote") ?? container;
                const referenceRect = positionedAncestor.getBoundingClientRect();
                const iconWidth = iconRect.width || 18;
                const desiredLeft = lastCharRect.right - referenceRect.left + 8;
                // The blockquote's own width is a text-wrapping constraint, not
                // a visual clipping boundary — it's centered with plenty of
                // page margin around it, so clamping against it (rather than
                // the actual viewport, which is what `overflow-x: hidden`
                // really clips against) was cutting the icon into the text
                // whenever the last line nearly filled the blockquote's width.
                const viewportRight = document.documentElement.clientWidth;
                const maxLeft = Math.max(0, viewportRight - referenceRect.left - iconWidth);
                const finalLeft = Math.min(desiredLeft, maxLeft);
                const finalTop = lastCharRect.top - referenceRect.top + lastCharRect.height / 2;
                icon.style.position = "absolute";
                // The stylesheet's margin-left still applies to an absolutely
                // positioned box (it offsets the margin edge from `left`), which
                // would double the gap already baked into desiredLeft.
                icon.style.marginLeft = "0";
                icon.style.top = `${finalTop}px`;
                icon.style.left = `${finalLeft}px`;
                icon.style.transform = "translateY(-50%)";
            }
        };

        reposition();

        // Web fonts swapping in after first paint (FOUT) reflow text without
        // changing the container's width, so the ResizeObserver below never
        // catches it — quotes near the top of the page are more likely to
        // have their initial measurement race the font swap than quotes
        // further down, which tend to lay out after fonts have settled.
        let cancelled = false;
        document.fonts?.ready?.then(() => {
            if (!cancelled) reposition();
        });

        // Toggling the icon between in-flow and absolute changes the
        // container's own height, which would otherwise re-trigger this same
        // observer in a loop. Line wrapping only ever depends on width, so
        // only re-measure when the width actually changes.
        let lastWidth = container.getBoundingClientRect().width;
        const resizeObserver = new ResizeObserver((entries) => {
            const width = entries[0]?.contentRect.width ?? container.getBoundingClientRect().width;
            if (Math.abs(width - lastWidth) < 1) return;
            lastWidth = width;
            reposition();
        });
        resizeObserver.observe(container);

        return () => {
            cancelled = true;
            resizeObserver.disconnect();
        };
    }, [children]);

    return (
        <blockquote className={`blockquote relative${wide ? " blockquote-wide" : ""} [&:has(+&)+&_span]:text-grey [&:has(+&)+&_span]:font-medium! [&:has(+&)+&_span]:font-mono! [&:has(+&)+&_span]:uppercase [&:has(+&)+&_span::before]:hidden [&:has(+&)+&_span_.blockquote-end-icon]:hidden [&:has(+&)+&_span]:text-[9px]! [&:has(+&)+&]:mb-10! [&:has(+&)+&]:mt-0! [&:has(+&)+&]:pt-6! [&:has(+&)+&]:tracking-lg!`}>
            <span ref={containerRef} className="leading-[130%] text-[32px] font-normal text-white">
                {children}
                <span ref={iconRef} className="blockquote-end-icon" aria-hidden="true" />
            </span>
        </blockquote>
    )
}
